// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { FacebookPixelUtils } from "../../../../../../../io/hyperify/core/facebook/FacebookPixelUtils";
import { ShoppingCartUtils } from "../../../../../../../io/hyperify/core/store/utils/ShoppingCartUtils";
import { Currency } from "../../../../../../../io/hyperify/core/types/Currency";
import { AuthSessionData } from "../../../../../../../io/hyperify/frontend/hooks/useAuthSession";
import { UTMService } from "../../../../../../../io/hyperify/frontend/services/UTMService";
import { FACEBOOK_PIXEL_ID } from "../../../../../core/constants/ads";
import { MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME } from "../../../../../core/constants/className";
import {
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_ADDRESS_LABEL,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_BACK_BUTTON,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_BUSINESS_ID_LABEL,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_BUSINESS_NAME_LABEL,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_CART_LABEL,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_EMAIL_LABEL,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_ERROR_MESSAGE,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_ID_LABEL,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_MOBILE_LABEL,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_NAME_LABEL,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_PHONE_LABEL,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_SUBMIT_BUTTON,
    T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_TITLE
} from "../../../../../core/constants/translation";
import { Form } from "../../../../../../../io/hyperify/frontend/components/form/Form";
import { ClientDTO } from "../../../../../../../io/hyperify/core/store/types/client/ClientDTO";
import { isArray } from "../../../../../../../io/hyperify/core/types/Array";
import { Button } from "../../../../../../../io/hyperify/frontend/components/button/Button";
import { useCallback, useState } from "react";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import { SubmitButton } from "../../../../../../../io/hyperify/frontend/components/submitButton/SubmitButton";
import { ComponentUtils } from "../../../../../../../io/hyperify/frontend/components/utils/ComponentUtils";
import { CartDetails } from "../../../common/cart/CartDetails";
import { useShoppingCart } from "../../../../../../../io/hyperify/frontend/hooks/useShoppingCart";
import { SendanorClientHttpService } from "../../../../../core/services/SendanorClientHttpService";
import { createNewOrderDTO } from "../../../../../../../io/hyperify/core/store/types/order/NewOrderDTO";
import { OrderDTO } from "../../../../../../../io/hyperify/core/store/types/order/OrderDTO";
import { useCartUpdatedEvent } from "../../../../hooks/useCartUpdatedEvent";
import { ButtonStyle } from "../../../../../../../io/hyperify/core/frontend/button/ButtonStyle";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import "./MyOrderConfirmSelectedClientArticle.scss";
import { FacebookPixel } from "../../../common/facebookPixel/FacebookPixel";

const LOG = LogService.createLogger('MyOrderConfirmSelectedClientArticle');

export interface MyOrderConfirmSelectedClientArticleProps {
    readonly className?: string;
    readonly t: TranslationFunction;
    readonly session: AuthSessionData;
    readonly client : ClientDTO;
    readonly backAction : () => void;
    readonly submitOrder : (order: OrderDTO) => void;
}

export function MyOrderConfirmSelectedClientArticle (props: MyOrderConfirmSelectedClientArticleProps) {

    const className = props?.className;
    const t = props?.t;
    const session = props?.session;
    const sessionToken = session?.token;
    const email = session?.email;
    const sms = session?.sms;
    const client = props?.client;
    const clientId = client?.id;
    const backActionProp = props?.backAction;
    const submitActionProp = props?.submitOrder;
    const [cart, refreshCart] = useShoppingCart();
    const myRefreshCartCallback = useCallback(
        () => {
            LOG.debug(`Cart updated`);
            refreshCart();
        },
        [
            refreshCart
        ]
    );
    useCartUpdatedEvent(myRefreshCartCallback);

    const cartItems = cart?.items ?? [];
    const totalSum: number = ShoppingCartUtils.getTotalSum(cartItems);

    const [hasError, setError] = useState<boolean>(false);

    const translationParams = {
        EMAIL: email,
        SMS: sms,
    };

    const submitAction = useCallback(
        (order: OrderDTO) => {
            if (submitActionProp) {
                submitActionProp(order);
            } else {
                LOG.warn(`No submitAction prop defined`);
            }
        },
        [
            submitActionProp
        ]
    );

    const backAction = useCallback(
        () => {
            if (backActionProp) {
                backActionProp();
            } else {
                LOG.warn(`No backAction prop defined`);
            }
        },
        [
            backActionProp
        ]
    );

    const onSubmit = useCallback(
        () => {
            LOG.debug(`Submit: `, clientId, cart);
            if (clientId && sessionToken) {
                setError(false);
                SendanorClientHttpService.sendMyOrder(
                    clientId,
                    createNewOrderDTO(
                        clientId,
                        cart,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        {
                            utm: UTMService.getUTMParamsAsJSON(),
                        },
                    ),
                    sessionToken
                ).then((order: OrderDTO) => {
                    submitAction(order);
                }).catch(err => {
                    LOG.error(`Error in ordering: `, err);
                    setError(true);
                });
            } else {
                LOG.error(`Client ID or sessionToken missing: `, clientId, sessionToken);
                setError(true);
            }
        },
        [
            clientId,
            cart,
            sessionToken,
            setError,
            submitAction
        ]
    );

    return (
        <article
            className={
                MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME
                + (className ? ` ${className}` : '')
            }
        >
            <Form className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-form`}
                  submit={onSubmit}
            >

                <h3>{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_TITLE)}</h3>

                <div className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-details`}>

                    <div className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-details-field details-id`}>
                        <strong>{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_ID_LABEL)}</strong><br />
                        {client.id}
                    </div>

                    <div className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-details-field details-name`}>
                        <strong>{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_NAME_LABEL)}</strong><br />
                        {client.firstname} {client.lastname}
                    </div>

                    {client?.company ? (
                        <div className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-details-field details-business-name`}>
                            <strong>{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_BUSINESS_NAME_LABEL)}</strong><br />
                            {client.company}
                        </div>
                    ) : null}

                    {client?.companyCode ? (
                        <div className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-details-field details-business-id`}>
                            <strong>{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_BUSINESS_ID_LABEL)}</strong><br />
                            {client.companyCode}
                        </div>
                    ): null}

                    <div className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-details-field details-email`}>
                        <strong>{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_EMAIL_LABEL)}</strong><br />
                        {client?.email}
                    </div>

                    {client?.phone ? (
                        <div className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-details-field details-phone`}>
                            <strong>{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_PHONE_LABEL)}</strong><br />
                            {client?.phone}
                        </div>
                    ) : null}

                    {client?.mobile ? (
                    <div className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-details-field details-mobile`}>
                        <strong>{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_MOBILE_LABEL)}</strong><br />
                        {client?.mobile}
                    </div>
                    ) : null}

                    <div className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-details-field details-address`}>
                        <strong>{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_ADDRESS_LABEL)}</strong><br />
                        {ComponentUtils.splitWithLineBreaks( isArray(client?.address) ? client.address.join('\n') : '')}<br />
                        {client?.postCode} {client?.postName}<br />
                        {client?.country}
                    </div>

                    <div className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-details-field details-cart`}>
                        <strong>{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_CART_LABEL)}</strong><br />
                        <CartDetails
                            className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-cart-details`}
                            t={t}
                            cart={cart}
                        />
                    </div>

                </div>

                <div className={`${MY_ORDER_CONFIRM_SELECTED_CLIENT_ARTICLE_CLASS_NAME}-buttons`}>
                    <Button
                        className={"button"}
                        style={ButtonStyle.SECONDARY}
                        click={backAction}
                    >{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_BACK_BUTTON, translationParams)}</Button>
                    <SubmitButton
                        className={"button"}
                    >{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_SUBMIT_BUTTON, translationParams)}</SubmitButton>
                </div>

                {hasError ? (
                    <p><strong>{t(T_MY_ORDER_VIEW_CONFIRM_SELECTED_CLIENT_ERROR_MESSAGE)}</strong></p>
                ) : null}

                <FacebookPixel url={FacebookPixelUtils.getLeadURL(FACEBOOK_PIXEL_ID, totalSum, Currency.EUR )} />

            </Form>
        </article>
    );

}
